import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import {
  getMe,
  getUserChannels,
  getUserOrgs,
  getUserStreams,
} from '../../../api/users';

import { logoutUser } from '../../Login/Form/LoginFormSlice';
import { REQUEST } from '../../../constants/request';

export const fetchMeUserData = createAsyncThunk(
  'currentUser/fetchMeUserData',
  async (params, { signal, rejectWithValue, dispatch }) => {
    const { CancelToken } = axios;
    let meCancellation;
    let channelCancellation;
    let streamCancellation;
    let orgCancellation;

    signal.addEventListener('abort', () => {
      meCancellation.cancel();
      channelCancellation.cancel();
      streamCancellation.cancel();
      orgCancellation.cancel();
    });

    return Promise.all([
      getMe({
        cancelToken: new CancelToken(c => {
          meCancellation = c;
        }),
      }),
      getUserChannels({
        cancelToken: new CancelToken(c => {
          channelCancellation = c;
        }),
      }),
      getUserStreams({
        cancelToken: new CancelToken(c => {
          streamCancellation = c;
        }),
      }),
      getUserOrgs({
        cancelToken: new CancelToken(c => {
          orgCancellation = c;
        }),
      }),
    ])
      .then(([userData, channelData, streamsData, orgsData]) => ({
        user: userData.data,
        channels: channelData.data,
        streams: streamsData.data,
        organizations: orgsData.data,
      }))
      .catch(error => {
        if (error.response.status === 404) {
          dispatch(logoutUser());
        }

        return rejectWithValue(error.response.status);
      });
  },
  {
    condition: (param, { getState }) => {
      const { loading } = getState().currentUser;

      return loading !== REQUEST.pending;
    },
  },
);

export const fetchMeUserDataExtraReducers = {
  [fetchMeUserData.pending]: (state, action) => {
    if (state.loading === REQUEST.idle) {
      state.loading = REQUEST.pending;
      state.currentRequestId = action.meta.requestId;
    }
  },
  [fetchMeUserData.fulfilled]: (state, action) => {
    const { meta, payload } = action;
    const { requestId } = meta;

    if (
      state.loading === REQUEST.pending &&
      state.currentRequestId === requestId
    ) {
      state.loading = REQUEST.idle;
      state.currentRequestId = undefined;
      state.user = payload.user;
      state.channels = payload.channels;
      state.streams = payload.streams;
      state.organizations = payload.organizations;
      state.threadChannels = [];

      const channelsByRoles = {};
      Object.entries(payload.channels).map(([channel, roles]) =>
        roles.map(role => {
          if (!channelsByRoles[role]) {
            channelsByRoles[role] = [];
          }
          channelsByRoles[role].push(channel);

          return role;
        }),
      );
      state.channelsByRoles = channelsByRoles;

      const orgsByRoles = {};
      Object.entries(payload.organizations).map(([org, roles]) =>
        roles.map(role => {
          if (!orgsByRoles[role]) {
            orgsByRoles[role] = [];
          }
          orgsByRoles[role].push(org);

          return role;
        }),
      );
      state.orgsByRoles = orgsByRoles;

      const streamsByRoles = {};
      Object.entries(payload.streams).map(([stream, roles]) =>
        roles.map(role => {
          if (!streamsByRoles[role]) {
            streamsByRoles[role] = [];
          }
          streamsByRoles[role].push(stream);

          return role;
        }),
      );
      state.streamsByRoles = streamsByRoles;

      localStorage.setItem('redux_current_user', JSON.stringify(state));
    }
  },
  [fetchMeUserData.rejected]: (state, action) => {
    const { requestId } = action.meta;
    if (
      state.loading === REQUEST.pending &&
      state.currentRequestId === requestId
    ) {
      state.loading = REQUEST.idle;
      state.error = action.payload;
      state.currentRequestId = undefined;
    }
  },
};
